import { useEffect, useMemo } from 'react';

import { atoms, Heading, VStack } from '@rtkwlf/fenrir-react';
import type { EditScannerConfigInputV2 } from '@rtkwlf/rendall';
import { ScannerConnectionStatus } from '@rtkwlf/rendall';
import type { UseFormReturn } from 'react-hook-form';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import { useEditScannerDetailsV2 } from '@apis/rendall/queries/use-edit-scanner-config-v2';
import { useScannerDetails } from '@apis/rendall/queries/use-scanner-details/use-scanner-details';
import { removeLeadingZerosFromIpOctets } from '@packages/utils/ip/ip';
import { EventKey } from 'ae-constants/constants';

import { scannerDetailSelect } from '../../../../../../../../../../hooks/use-enhanced-scanner-details/use-enhanced-scanner-details';
import { ScannerConfigFormControls } from '../../../../scanner-config-form-controls/scanner-config-form-controls';
import { useScannerConfigurationContext } from '../../../../scanner-configuration-context-provider';
import { AddIPControls } from '../../../components/add-ip-controls/add-ip-controls';
import { isAddressInCidrRange } from '../../is-address-in-cidr-range/is-address-in-cidr-range';
import { ScanExclusionTable } from '../scan-exclusion-table/scan-exclusion-table';

export type ScanExclusionType = { ipRange: string; ports: string };

export type AddIpRangeForm = {
  newExclusion: ScanExclusionType;
  editExclusion: ScanExclusionType;
  scanExclusions: Array<ScanExclusionType>;
};

const sortFn = (ipRangeA: string, ipRangeB: string) => {
  if (ipRangeA > ipRangeB) return 1;
  if (ipRangeA < ipRangeB) return -1;
  return 0;
};

export const AddIPRangeForm: React.FC = () => {
  const { t } = useTranslation('telemetryScannerConfig', {
    keyPrefix: 'scanExclusion',
  });
  const { scannerId = '' } = useParams();
  const { setIsFormDirty } = useScannerConfigurationContext();

  const {
    data: scannerDetails,
    isPending,
    isSuccess,
  } = useScannerDetails(scannerId, { select: scannerDetailSelect });

  const hasPortExemptionCapability = scannerDetails?.capabilities.includes(
    'port_scan_exemption',
  );

  const formMethods = useForm<AddIpRangeForm>({
    defaultValues: {
      newExclusion: {
        ipRange: '',
        ports: '',
      },
      editExclusion: {
        ipRange: '',
        ports: '',
      },
      scanExclusions: [],
    },
  });

  const {
    getValues,
    setValue,
    trigger,
    reset,
    resetField,
    formState: { errors, isDirty, dirtyFields },
    control,
  } = formMethods;

  useEffect(() => {
    if (isSuccess) {
      const scanExclusionDenyList: Array<ScanExclusionType> =
        scannerDetails?.denyList?.map((denyList) => ({
          ipRange: denyList,
          ports: '',
        })) || [];

      reset({
        scanExclusions: [
          ...scannerDetails.portBlockRules,
          ...scanExclusionDenyList,
        ].sort((a, b) => sortFn(a.ipRange, b.ipRange)),
      });
    }
  }, [isSuccess, reset, scannerDetails]);

  const handleAdd = async () => {
    const { ipRange, ports } = getValues('newExclusion');
    const trimmedIp = removeLeadingZerosFromIpOctets(ipRange.trim());
    const currentScanExclusionEntries = getValues('scanExclusions');
    const foundInScanExclusionEntries = currentScanExclusionEntries.some(
      ({ ipRange }) => {
        if (ipRange.includes('/')) {
          return isAddressInCidrRange(trimmedIp, ipRange);
        } else {
          return ipRange === trimmedIp;
        }
      },
    );

    if (!foundInScanExclusionEntries) {
      const isValid = await trigger('newExclusion');
      if (!isValid) return;

      setValue(
        'scanExclusions',
        [...currentScanExclusionEntries, { ipRange: trimmedIp, ports }].sort(
          (a, b) => sortFn(a.ipRange, b.ipRange),
        ),
        {
          shouldDirty: true,
        },
      );
      resetField('newExclusion', {
        defaultValue: { ipRange: '', ports: '' },
        keepDirty: false,
      });
      resetField('editExclusion', {
        defaultValue: { ipRange: '', ports: '' },
        keepDirty: false,
      });
      return;
    }

    control.setError(
      'newExclusion.ipRange',
      { message: t('validation.ipAddressNotUnique') },
      { shouldFocus: true },
    );

    return;
  };

  const { mutate: editScannerConfigV2, isPending: updatePending } =
    useEditScannerDetailsV2(scannerId);

  const onSubmit = () => {
    if (scannerDetails) {
      const editObject: EditScannerConfigInputV2 = {
        hostIdentificationScanIntensity:
          scannerDetails.hostIdentificationScanIntensity,
        hostIdentificationScanPeriod:
          scannerDetails.hostIdentificationScanPeriod,
        maxConcurrentVulnerabilityScans:
          scannerDetails.maxConcurrentVulnerabilityScans,
        name: null,
        site: null,
        hostIden: scannerDetails.hostIdentificationScans,
        vulnScan: scannerDetails.vulnerabilityScans,
        cgiScan: scannerDetails.CGIScanning,
        bruteScan: scannerDetails.bruteForceChecks,
        pingOnly: scannerDetails.onlyPingTheTarget,
        hostIdenServers: scannerDetails.dnsServers?.sort() || [],
        scanExclusionList: getValues('scanExclusions')
          .filter((item) => !item.ports)
          .map((item) => item.ipRange),
        portBlockRules: getValues('scanExclusions')
          .filter((item) => item.ports)
          .map((item) => {
            return {
              target: item.ipRange,
              ports: item.ports.split(/,\s*/),
            };
          }),
      };

      editScannerConfigV2(editObject, {
        onSuccess: () => {
          reset({
            scanExclusions: getValues('scanExclusions'),
          });
        },
      });
    }
  };

  const isTableDirty = useMemo(
    () =>
      dirtyFields.scanExclusions?.some(
        ({ ipRange, ports }) => ipRange || ports,
      ) ?? false,
    [dirtyFields.scanExclusions],
  );

  useEffect(() => {
    setIsFormDirty(isTableDirty);
  }, [isTableDirty, setIsFormDirty]);

  const isSubmitDisabled =
    scannerDetails?.connectionStatus !== ScannerConnectionStatus.Connected ||
    !isTableDirty ||
    isPending;

  const preventSubmitOnEnter = (e: React.KeyboardEvent<HTMLFormElement>) => {
    if (e.key === EventKey.Return) e.preventDefault();
  };

  const scanExclusions = getValues('scanExclusions');

  return (
    <FormProvider {...formMethods}>
      <form
        className={atoms({ width: 'full' })}
        onKeyDown={preventSubmitOnEnter}
      >
        <VStack gap="medium" width="full">
          <AddIPControls
            portsParams={{
              visible: true,
              disabled: !hasPortExemptionCapability || false,
              disabledHelperText: t('oldScanner'),
            }}
            ipControlsItemKey="newExclusion"
            ipControlsListKey="scanExclusions"
            onAdd={handleAdd}
            errors={errors}
            formMethods={formMethods as unknown as UseFormReturn}
          />
          <Heading level="4" paddingY="xsmall">
            {t('scanExclusionList')}
          </Heading>
          <ScanExclusionTable tableData={scanExclusions} />
          <ScannerConfigFormControls
            isDirty={isDirty}
            isDisabled={isSubmitDisabled}
            isPending={updatePending}
            handleClick={onSubmit}
            reset={reset}
          />
        </VStack>
      </form>
    </FormProvider>
  );
};
